import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  House, AirplaneTilt, Package, Wallet, ChatCircleDots, Bell, Star, ShieldCheck,
  Gear, SignOut, Plus, ArrowUpRight, ArrowDownRight, CheckCircle, Clock, MapPin, CurrencyDollar, TrendUp, Lock
} from '@phosphor-icons/react';
import { API } from '@/App';
import { toast } from 'sonner';
import DropioLogo from '@/components/DropioLogo';

const SIDEBAR_ITEMS = [
  { id: 'overview', icon: House, label: 'Overview' },
  { id: 'my-journeys', icon: AirplaneTilt, label: 'My Journeys' },
  { id: 'requests', icon: Package, label: 'Requests' },
  { id: 'messages', icon: ChatCircleDots, label: 'Messages', path: '/messages' },
  { id: 'wallet', icon: Wallet, label: 'Wallet', path: '/wallet' },
  { id: 'reviews', icon: Star, label: 'Reviews' },
  { id: 'verification', icon: ShieldCheck, label: 'Verification', path: '/kyc' },
  { id: 'settings', icon: Gear, label: 'Settings' }
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState('overview');
  const [user, setUser] = useState(null);
  const [trips, setTrips] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [sentBookings, setSentBookings] = useState([]);
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const u = await fetch(`${API}/auth/me`, { credentials: 'include' }).then((r) => r.json());
        setUser(u);
        const isTraveler = u.user_type === 'traveler' || u.user_type === 'admin';
        const isSender = u.user_type === 'sender' || u.user_type === 'admin';
        const [t, b, p, sb] = await Promise.all([
          isTraveler ? fetch(`${API}/trips/my`, { credentials: 'include' }).then((r) => r.json()) : [],
          isTraveler ? fetch(`${API}/bookings/received`, { credentials: 'include' }).then((r) => r.json()) : [],
          isSender ? fetch(`${API}/parcels/my`, { credentials: 'include' }).then((r) => r.json()) : [],
          isSender ? fetch(`${API}/bookings/sent`, { credentials: 'include' }).then((r) => r.json()) : []
        ]);
        setTrips(t); setBookings(b); setParcels(p); setSentBookings(sb);
        setLoading(false);
      } catch (e) {
        toast.error('Failed to load dashboard');
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleLogout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/');
  };

  const handleBookingAction = async (rid, status) => {
    await fetch(`${API}/bookings/${rid}/status`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ status })
    });
    toast.success(`Booking ${status}`);
    const b = await fetch(`${API}/bookings/received`, { credentials: 'include' }).then((r) => r.json());
    setBookings(b);
  };

  const handlePay = async (booking) => {
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ booking_id: booking.request_id, origin_url: window.location.origin })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Checkout failed');
      window.location.assign(data.url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleConfirmDelivery = async (booking) => {
    try {
      const res = await fetch(`${API}/bookings/${booking.request_id}/confirm-delivery`, {
        method: 'POST', credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Confirmation failed');
      toast.success(`Delivery confirmed! $${data.traveler_payout.toFixed(2)} released to traveler.`);
      const sb = await fetch(`${API}/bookings/sent`, { credentials: 'include' }).then((r) => r.json());
      setSentBookings(sb);
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D28D9]"></div>
      </div>
    );
  }

  const isTraveler = user?.user_type === 'traveler';
  const earnings = bookings.filter((b) => b.status === 'accepted').reduce((s, b) => s + (b.requested_price || 0), 0);
  const pendingCount = bookings.filter((b) => b.status === 'pending').length;
  const acceptedCount = bookings.filter((b) => b.status === 'accepted').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:flex w-64 bg-white border-r border-gray-100 flex-col fixed h-screen" data-testid="sidebar">
          <div className="p-6 border-b border-gray-100">
            <Link to="/"><DropioLogo size={32} /></Link>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {SIDEBAR_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = active === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => item.path ? navigate(item.path) : setActive(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                    isActive ? 'dropio-gradient-cta text-white shadow-md' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid={`sidebar-${item.id}`}
                >
                  <Icon size={20} weight={isActive ? 'fill' : 'duotone'} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
          <div className="p-4 border-t border-gray-100">
            <div className="flex items-center gap-3 mb-3 p-2">
              <div className="w-10 h-10 rounded-full dropio-gradient flex items-center justify-center text-white font-bold flex-shrink-0">
                {user?.name?.charAt(0)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.user_type}</p>
              </div>
            </div>
            <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid="sidebar-logout">
              <SignOut size={18} weight="bold" /> Log out
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64 p-6 md:p-8">
          {/* Top Bar */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="dashboard-title">
                Welcome back, {user?.name?.split(' ')[0]}! 👋
              </h1>
              <p className="text-gray-600 mt-1">{isTraveler ? "Here's what's happening with your journeys." : "Manage your parcels and find travelers."}</p>
            </div>
            <div className="flex items-center gap-3">
              <button className="relative p-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="notifications-button">
                <Bell size={20} weight="duotone" className="text-gray-700" />
                {pendingCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">{pendingCount}</span>
                )}
              </button>
              {isTraveler ? (
                <button onClick={() => navigate('/trips/create')} className="dropio-gradient-cta text-white font-semibold px-5 py-2.5 rounded-lg shadow-md flex items-center gap-2" data-testid="new-journey-button">
                  <Plus size={16} weight="bold" /> New Journey
                </button>
              ) : (
                <button onClick={() => navigate('/parcels/create')} className="dropio-gradient-cta text-white font-semibold px-5 py-2.5 rounded-lg shadow-md flex items-center gap-2" data-testid="new-parcel-button">
                  <Plus size={16} weight="bold" /> New Parcel
                </button>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Earnings" value={`$${earnings.toFixed(0)}`} delta="+12%" icon={Wallet} positive />
            <StatCard label={isTraveler ? 'Active Journeys' : 'Active Parcels'} value={isTraveler ? trips.length : parcels.length} icon={AirplaneTilt} />
            <StatCard label="Pending Requests" value={pendingCount} icon={Clock} />
            <StatCard label="Trust Score" value={`${user?.rating ? user.rating.toFixed(1) : '0.0'}`} icon={Star} suffix="/5.0" />
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left/Main column */}
            <div className="lg:col-span-2 space-y-6">
              {/* My Journey / Parcels card */}
              {isTraveler ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-lg font-bold text-gray-900">My Journeys</h2>
                    <Link to="/trips/create" className="text-sm font-semibold text-[#6D28D9] hover:underline">View all →</Link>
                  </div>
                  {trips.length === 0 ? (
                    <EmptyState icon={AirplaneTilt} title="No journeys yet" desc="Post your first trip to start earning" cta="Create Journey" onClick={() => navigate('/trips/create')} />
                  ) : (
                    <div className="space-y-3">
                      {trips.slice(0, 3).map((t) => (
                        <Link key={t.trip_id} to={`/trips/${t.trip_id}`} className="block p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl hover:shadow-md transition-all" data-testid={`journey-card-${t.trip_id}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-full dropio-gradient flex items-center justify-center text-white">
                                <AirplaneTilt size={22} weight="duotone" />
                              </div>
                              <div>
                                <p className="font-bold text-gray-900">{t.departure_city} <span className="text-[#6D28D9]">→</span> {t.destination_city}</p>
                                <p className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                                  <Clock size={12} weight="bold" /> {new Date(t.departure_date).toLocaleDateString('en', { day: 'numeric', month: 'short' })} • {t.available_weight}kg
                                  {t.capacity_mode === 'single' && (
                                    <span className="text-[10px] font-bold uppercase bg-purple-100 text-[#6D28D9] px-1.5 py-0.5 rounded ml-1">Single</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-[#6D28D9]">${t.price_per_kg}<span className="text-xs text-gray-500">/kg</span></p>
                              <span className="text-[10px] font-bold uppercase text-green-700 bg-green-100 px-2 py-0.5 rounded">Active</span>
                            </div>
                          </div>
                          <TripCapacityBar tripId={t.trip_id} totalKg={t.available_weight} />
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-lg font-bold text-gray-900">My Parcels</h2>
                    <Link to="/parcels/create" className="text-sm font-semibold text-[#6D28D9] hover:underline">View all →</Link>
                  </div>
                  {parcels.length === 0 ? (
                    <EmptyState icon={Package} title="No parcels yet" desc="Create your first parcel to find a traveler" cta="Create Parcel" onClick={() => navigate('/parcels/create')} />
                  ) : (
                    <div className="space-y-3">
                      {parcels.slice(0, 3).map((p) => (
                        <div key={p.parcel_id} className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl" data-testid={`parcel-card-${p.parcel_id}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-full dropio-gradient flex items-center justify-center text-white">
                                <Package size={22} weight="duotone" />
                              </div>
                              <div>
                                <p className="font-bold text-gray-900">{p.origin_city} <span className="text-[#6D28D9]">→</span> {p.destination_city}</p>
                                <p className="text-xs text-gray-500">{p.category} • {p.weight}kg</p>
                              </div>
                            </div>
                            <span className="text-[10px] font-bold uppercase text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded">{p.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-5">My Bookings</h2>
                  {sentBookings.length === 0 ? (
                    <EmptyState icon={CurrencyDollar} title="No bookings yet" desc="When a traveler accepts your request, you can pay and track delivery here." />
                  ) : (
                    <div className="space-y-3">
                      {sentBookings.slice(0, 5).map((b) => (
                        <div key={b.request_id} className="p-4 border border-gray-100 rounded-xl" data-testid={`my-booking-${b.request_id}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-600">{b.traveler_name?.charAt(0)}</div>
                              <div>
                                <p className="font-bold text-gray-900 text-sm">{b.traveler_name}</p>
                                <p className="text-xs text-gray-500">{b.trip_details?.departure_city} → {b.trip_details?.destination_city}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-[#6D28D9]">${b.requested_price}</p>
                              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                                b.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                b.status === 'accepted' ? 'bg-blue-100 text-blue-700' :
                                b.status === 'paid' ? 'bg-purple-100 text-[#6D28D9]' :
                                b.status === 'delivered' ? 'bg-green-100 text-green-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>{b.status}</span>
                            </div>
                          </div>
                          {b.status === 'accepted' && (
                            <button onClick={() => handlePay(b)} className="w-full dropio-gradient-cta text-white text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-1.5" data-testid={`pay-${b.request_id}`}>
                              <Lock size={14} weight="bold" /> Pay ${(b.requested_price + 2).toFixed(2)} (incl. $2 fee)
                            </button>
                          )}
                          {b.status === 'paid' && (
                            <div className="space-y-2">
                              <button onClick={() => handleConfirmDelivery(b)} className="w-full bg-green-500 hover:bg-green-600 text-white text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-1.5" data-testid={`confirm-${b.request_id}`}>
                                <CheckCircle size={14} weight="bold" /> Confirm Delivery & Release Funds
                              </button>
                              <Link to={`/disputes?booking=${b.request_id}`} className="block text-center text-xs font-semibold text-gray-500 hover:text-red-600" data-testid={`dispute-link-${b.request_id}`}>
                                Need help? Open a dispute
                              </Link>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                </>
              )}

              {/* Matching Requests */}
              {isTraveler && (
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-lg font-bold text-gray-900">Matching Requests</h2>
                    <span className="text-xs font-bold text-white bg-[#6D28D9] px-2 py-0.5 rounded-full">{pendingCount} new</span>
                  </div>
                  {bookings.filter((b) => b.status === 'pending').length === 0 ? (
                    <EmptyState icon={Package} title="No pending requests" desc="When senders request your route, they'll appear here" />
                  ) : (
                    <div className="space-y-3">
                      {bookings.filter((b) => b.status === 'pending').slice(0, 3).map((b) => (
                        <div key={b.request_id} className="p-4 border border-gray-100 rounded-xl hover:shadow-md transition-all" data-testid={`request-${b.request_id}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-600">{b.sender_name?.charAt(0)}</div>
                              <div>
                                <p className="font-bold text-gray-900 text-sm">{b.sender_name}</p>
                                <p className="text-xs text-gray-500">{b.parcel_details?.category} • {b.parcel_details?.weight}kg</p>
                              </div>
                            </div>
                            <p className="text-lg font-bold text-[#6D28D9]">${b.requested_price}</p>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => handleBookingAction(b.request_id, 'accepted')} className="flex-1 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold py-2 rounded-lg" data-testid={`accept-${b.request_id}`}>Accept</button>
                            <button onClick={() => handleBookingAction(b.request_id, 'rejected')} className="flex-1 border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold py-2 rounded-lg" data-testid={`reject-${b.request_id}`}>Decline</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Trust Score */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wider mb-4">Trust Score</h3>
                <div className="text-center mb-4">
                  <div className="relative inline-block">
                    <svg className="w-32 h-32 -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="42" fill="none" stroke="#F3F4F6" strokeWidth="8" />
                      <circle cx="50" cy="50" r="42" fill="none" stroke="url(#tscore)" strokeWidth="8" strokeLinecap="round" strokeDasharray={`${((user?.rating || 0) / 5) * 264} 264`} />
                      <defs>
                        <linearGradient id="tscore" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#6D28D9" />
                          <stop offset="100%" stopColor="#EC4899" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <p className="text-3xl font-extrabold text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{(user?.rating || 0).toFixed(1)}</p>
                      <p className="text-xs text-gray-500">out of 5</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <ScoreRow label="Identity verified" status={user?.verification_status === 'verified'} />
                  <ScoreRow label="Email confirmed" status={true} />
                  <ScoreRow label="Phone verified" status={!!user?.phone} />
                  <ScoreRow label="Address verified" status={false} />
                </div>
              </div>

              {/* Recent Notifications */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wider mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {bookings.slice(0, 4).map((b) => (
                    <div key={b.request_id} className="flex gap-3 text-sm">
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${b.status === 'pending' ? 'bg-yellow-500' : b.status === 'accepted' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <div className="flex-1">
                        <p className="text-gray-900"><strong>{b.sender_name}</strong> {b.status === 'pending' ? 'sent you a request' : b.status === 'accepted' ? 'booking confirmed' : 'declined'}</p>
                        <p className="text-xs text-gray-500">${b.requested_price} • {new Date(b.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                  {bookings.length === 0 && <p className="text-sm text-gray-500 text-center py-4">No recent activity</p>}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, delta, icon: Icon, positive, suffix = '' }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5">
    <div className="flex items-start justify-between mb-3">
      <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
        <Icon size={20} weight="duotone" className="text-[#6D28D9]" />
      </div>
      {delta && (
        <span className={`text-xs font-bold flex items-center gap-0.5 ${positive ? 'text-green-600' : 'text-red-600'}`}>
          {positive ? <ArrowUpRight size={12} weight="bold" /> : <ArrowDownRight size={12} weight="bold" />}
          {delta}
        </span>
      )}
    </div>
    <p className="text-2xl font-extrabold text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
      {value}<span className="text-sm text-gray-500 font-semibold">{suffix}</span>
    </p>
    <p className="text-xs text-gray-500 font-medium mt-0.5">{label}</p>
  </div>
);

const ScoreRow = ({ label, status }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-gray-700">{label}</span>
    {status ? <CheckCircle size={18} weight="fill" className="text-green-500" /> : <span className="text-xs text-gray-400">Pending</span>}
  </div>
);

const EmptyState = ({ icon: Icon, title, desc, cta, onClick }) => (
  <div className="text-center py-8">
    <Icon size={40} weight="duotone" className="text-gray-300 mx-auto mb-3" />
    <p className="font-semibold text-gray-900 mb-1">{title}</p>
    <p className="text-sm text-gray-500 mb-4">{desc}</p>
    {cta && (
      <button onClick={onClick} className="dropio-gradient-cta text-white text-sm font-semibold px-4 py-2 rounded-lg">
        {cta}
      </button>
    )}
  </div>
);

const TripCapacityBar = ({ tripId, totalKg }) => {
  const [cap, setCap] = useState(null);
  useEffect(() => {
    fetch(`${API}/trips/${tripId}/capacity`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setCap(d))
      .catch(() => {});
  }, [tripId]);
  if (!cap) return null;
  const pct = cap.utilisation_percent || 0;
  return (
    <div className="mt-3" data-testid={`capacity-bar-${tripId}`} onClick={(e) => e.preventDefault()}>
      <div className="flex items-center justify-between text-[11px] text-gray-600 font-semibold mb-1">
        <span>Capacity</span>
        <span data-testid={`capacity-text-${tripId}`}>{cap.booked_kg}/{cap.total_kg}kg ({pct}%)</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
        <div className="h-full dropio-gradient" style={{ width: `${Math.min(100, pct)}%` }}></div>
      </div>
      {cap.is_full && (
        <p className="text-[10px] font-bold uppercase text-red-600 mt-1">Full · no more bookings</p>
      )}
    </div>
  );
};

export default Dashboard;
